import { format } from "date-fns";
import { DateTime } from "luxon";

export interface TimeOption {
  value: string; // 24-hour format (e.g., "09:00", "13:30")
  label: string; // 12-hour format (e.g., "9:00 AM", "1:30 PM")
}

/**
 * Generate an array of 30-minute time intervals with both 24-hour values and 12-hour labels.
 * Used for time selection dropdowns in forms.
 *
 * @returns Array of TimeOption objects with value (24h) and label (12h) properties
 */
export function generateTimeOptions(): TimeOption[] {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const value = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
      const displayTime = format(new Date(`2000-01-01T${value}`), "h:mm aa");

      options.push({
        value,
        label: displayTime,
      });
    }
  }

  return options;
}

/**
 * Generate an array of 30-minute increments in a 12-hour format.
 * This function matches the DatePicker component's generateHalfHourIncrements12h function.
 *
 * @returns Array of time strings in 12-hour format (e.g., ["12:00 AM", "12:30 AM", ...])
 */
export function generateHalfHourIncrements12h(): string[] {
  const times: string[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const d1 = new Date(2000, 0, 1, hour, 0);
    const d2 = new Date(2000, 0, 1, hour, 30);

    times.push(format(d1, "h:mm aa"));
    times.push(format(d2, "h:mm aa"));
  }

  return times;
}

/**
 * Convert a UTC datetime string to display format in a specific timezone.
 * Uses the same logic as DatePicker component for consistency.
 *
 * @param utcDateString - ISO string of the UTC date
 * @param timeZone - Target timezone identifier (e.g., "America/New_York")
 * @returns Object with date, startTime, and endTime for display
 */
export function convertUtcToTimezone(
  utcDateString: string,
  timeZone: string,
): { date: string; time: string } {
  const dateTime = DateTime.fromISO(utcDateString).setZone(timeZone);

  return {
    date: dateTime.toFormat("yyyy-MM-dd"),
    time: dateTime.toFormat("HH:mm"),
  };
}

/**
 * Convert timezone-aware time back to UTC using the same logic as DatePicker.
 * This ensures consistency with how session start/end dates are handled.
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @param timeZone - Source timezone identifier
 * @returns UTC Date object
 */
export function convertTimezoneToUtc(
  date: string,
  time: string,
  timeZone: string,
): Date {
  const [hours, minutes] = time.split(":").map(Number);

  let dt = DateTime.fromFormat(date, "yyyy-MM-dd").setZone(timeZone);
  dt = dt.set({ hour: hours, minute: minutes });

  return dt.toUTC().toJSDate();
}

/**
 * Format a duration in seconds to a human-readable time string.
 * Examples: 65 → "1:05", 3661 → "1:01:01"
 */
export function formatDuration(totalSeconds: number): string {
  if (isNaN(totalSeconds)) return "0:00";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format a timestamp string like "00:00:06.990" to a compact time display.
 * Strips milliseconds and leading zero hours.
 * Examples: "00:01:30.500" → "1:30", "01:02:03.000" → "1:02:03"
 */
export function formatTimestampToTime(timestamp: string): string {
  if (!timestamp) return "";

  const parts = timestamp.split(".");
  const timePart = parts[0];
  const timeSegments = timePart.split(":");

  if (timeSegments.length === 3) {
    const hours = parseInt(timeSegments[0], 10);
    const minutes = parseInt(timeSegments[1], 10);
    const seconds = parseInt(timeSegments[2], 10);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }

  return timestamp;
}
